import dayjs, { type Dayjs } from "dayjs"

import { type Booking, type TimeRange } from "@/graphql/types"
import { getTypeObjectProperty, splitHours } from "@/utils/bookings"
import { calculateAge } from "@/utils/calendar"

export type WeightingKey =
  | "regularChild"
  | "nurseryChildOrUnder3"
  | "migration"
  | "assistedIntegration"
  | "schoolChild"

export type AttendanceWeightingFilter =
  | WeightingKey
  | "preCourse"
  | "preSchooler"
  | "lunch"
  | "slotSplitting"

export const WEIGHTING_FACTORS: Record<WeightingKey, number> = {
  regularChild: 1.0,
  schoolChild: 1.2,
  migration: 1.3,
  nurseryChildOrUnder3: 2.0,
  assistedIntegration: 4.5,
}

export const childrenWeightingColors: Record<WeightingKey, string> = {
  regularChild: "#97d94b",
  schoolChild: "#7bc9ff",
  migration: "#ff7bf1",
  nurseryChildOrUnder3: "#d9a054",
  assistedIntegration: "#997bff",
}

export const getBookingWeighting = (booking: Booking) => {
  const { assistedIntegration, migration, nurseryChild, isSchoolChild, isPreSchooler } = booking

  return {
    assistedIntegration,
    migration,
    nurseryChild,
    isSchoolChild,
    isPreSchooler,
  }
}

export const checkAttendanceWeightingFilter = (
  filter: AttendanceWeightingFilter | undefined,
  options: {
    weightingKey: WeightingKey
    booking?: Booking
    childBirthDate?: string
    referenceDate?: Dayjs
  }
): boolean => {
  if (!filter) return true

  const { weightingKey, booking, childBirthDate, referenceDate } = options
  const childAgeUnder3 = childBirthDate
    ? calculateAge(dayjs(childBirthDate), referenceDate) < 3
    : false
  const nurseryChildOrUnder3 = !!booking?.nurseryChild || childAgeUnder3
  const regularChild =
    !nurseryChildOrUnder3 &&
    !booking?.migration &&
    !booking?.assistedIntegration &&
    !booking?.isSchoolChild

  switch (filter) {
    case "regularChild":
      return booking ? regularChild : weightingKey === filter
    case "nurseryChildOrUnder3":
      return booking ? nurseryChildOrUnder3 : weightingKey === filter
    case "migration":
      return booking ? booking.migration : weightingKey === filter
    case "assistedIntegration":
      return booking ? booking.assistedIntegration : weightingKey === filter
    case "schoolChild":
      return booking ? booking.isSchoolChild : weightingKey === filter
    case "preCourse":
      return booking?.preCourse === true
    case "preSchooler":
      return booking?.isPreSchooler === true
    case "lunch":
      return booking?.lunch === true
    case "slotSplitting":
      return booking?.typeObject
        ? !!getTypeObjectProperty(booking.typeObject, "slotSplitting")
        : false
  }
}

// Attendance helpers shared with attendance views
export type AttendanceRow = {
  id: string
  person: { firstname?: string | null; lastname?: string | null }
  weightingKey: WeightingKey
  factor?: number
  times: TimeRange[]
}

export const toMinutes = (time: string): number | undefined => {
  const parts = splitHours(time)
  return parts ? parts[0] * 60 + parts[1] : undefined
}

export const earliestStart = (times?: TimeRange[]): number | undefined => {
  const starts = (times || [])
    .map((t) => toMinutes(t?.start))
    .filter((v): v is number => v !== undefined)
  return starts.length ? Math.min(...starts) : undefined
}

export const latestEnd = (times?: TimeRange[]): number | undefined => {
  const ends = (times || [])
    .map((t) => toMinutes(t?.end))
    .filter((v): v is number => v !== undefined)
  return ends.length ? Math.max(...ends) : undefined
}

export const compareNumbersWithOrder = (a?: number, b?: number, order: "asc" | "desc" = "asc") => {
  const dir = order === "asc" ? 1 : -1
  if (a === undefined) return b === undefined ? 0 : 1
  if (b === undefined) return -1
  return a === b ? 0 : (a < b ? -1 : 1) * dir
}

export const compareAlphabetical = (a: AttendanceRow, b: AttendanceRow) => {
  const an = `${a?.person.lastname ?? ""} ${a?.person.firstname ?? ""}`.toLocaleLowerCase()
  const bn = `${b?.person.lastname ?? ""} ${b?.person.firstname ?? ""}`.toLocaleLowerCase()
  if (an < bn) return -1
  if (an > bn) return 1
  return 0
}

export const compareArriving = (a: AttendanceRow, b: AttendanceRow): number => {
  const startA = earliestStart(a.times)
  const startB = earliestStart(b.times)
  const startCompare = compareNumbersWithOrder(startA, startB, "asc")
  if (startCompare !== 0) return startCompare
  const nameCompare = compareAlphabetical(a, b)
  if (nameCompare !== 0) return nameCompare
  return a.id.localeCompare(b.id)
}

export const compareLeaving = (a: AttendanceRow, b: AttendanceRow): number => {
  const endA = latestEnd(a.times)
  const endB = latestEnd(b.times)
  const endCompare = compareNumbersWithOrder(endA, endB, "asc")
  if (endCompare !== 0) return endCompare
  const nameCompare = compareAlphabetical(a, b)
  if (nameCompare !== 0) return nameCompare
  return a.id.localeCompare(b.id)
}
